<template>
  <div class="downloads-page">
    <button id="btnDownload" class="btn-primary" :disabled="isMobileClient || isDownloading" @click="downloadSafe">
      <spinner :is-spinning="isDownloading" />
      {{ tn("downloadNow") }}
    </button>
    <p v-show="isOutDatedWindowsResult" class="mt">
      <i18n-t keypath="DownloadSafeComponent.sha1Message" tag="span" class="account-availability" scope="global">
        <template #download>
          <strong>{{ t("DownloadSafeComponent.downloadingOnWindows") }}</strong>
        </template>
        <template #moreDetails>
          <a
            id="linkDownSafeMoreDetailsTargetBlank"
            :href="getUrl('VITE_APP_SAFE_SHA1_MORE_DETAILS_URL')"
            target="_blank"
          >
            {{ t("DownloadSafeComponent.moreDetails") }}
          </a>
        </template>
      </i18n-t>
    </p>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, computed, ref } from "vue";
import { logEvent, logException } from "@/common/logger";
import { t } from "@/i18n";
import { IDevice } from "@/common/api/unifiedPortal/interfaces";
import { useNotificationsStore } from "@/stores/notifications";
import { GetSafeClientAndDownload } from "@/common/safeClient";
import { isMobile, isOutDatedWindows, isSafari } from "@/common/browserDetect";
import { reloadSubscriberInfo } from "@/common/reloadSubscriberInfo";
import Spinner from "@/components/shared/Spinner.vue";
import { getUserDetailsFromStore } from "@/common/userDetails";
import { getUrl } from "@/common/getUrl";

const name = "DownloadSafeComponent";

export default defineComponent({
  name,
  components: { Spinner },
  props: {
    device: {
      type: Object as PropType<IDevice>,
      required: false,
      default: null,
    },
    installType: {
      type: String,
      required: false,
      default: null,
    },
  },
  emits: ["downloaded"],

  setup(props, context) {
    logEvent("created", name, props.device);
    const notificationsStore = useNotificationsStore();
    const isDownloading = ref<boolean>(false);

    function tn(v: string, params?: Record<string, unknown>): string {
      return t(`${name}.${v}`, params);
    }

    const isMobileClient = computed(() => {
      return isMobile();
    });

    const isOutDatedWindowsResult = computed(() => {
      return isOutDatedWindows();
    });

    async function downloadSafe() {
      isDownloading.value = true;

      try {
        notificationsStore.clearNotifications();
        logEvent("DownloadSafeEvent", name, "Emitted");
        // If null, attribute a default value
        const activationCode = props.device?.activationCode ? props.device.activationCode : "";
        const deviceId = props.device?.deviceId ? props.device.deviceId : 0;
        const installType = props.installType ? props.installType : "Install";
        const isSafeLiteDownload = props.device?.webrootLicenseKey && !props.device?.activationCode;

        const safeClient = isSafeLiteDownload
          ? await GetSafeClientAndDownload("", 0, "", "Install", props.device?.webrootLicenseKey ?? "")
          : await GetSafeClientAndDownload(activationCode, deviceId, "", installType, "");
        if (safeClient === "Ok") {
          // Enable update HasSafeSubscriptions and hasTrialSafe flags on user if download started for a Trial
          context.emit("downloaded");
        }
      } catch (error) {
        logException(error as Error);
      }

      if (!isSafari()) {
        await reloadSubscriberInfo();
        await getUserDetailsFromStore(true);
      }
      isDownloading.value = false;
    }
    return { t, tn, isMobileClient, downloadSafe, isOutDatedWindowsResult, isDownloading, getUrl };
  },
});
</script>

<style scoped lang="css">
@import "@/styles/variables.css";
</style>
